import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { store, RootState, AppDispatch } from './store';
import { AdminLayout } from './layouts/AdminLayout';
import { SellerLayout } from './layouts/SellerLayout';
import api from './services/api';
import { SELLER_EXTENDED_FLAG } from './config/rolePermissions';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Privacidad } from './pages/Privacidad';
import { Soporte } from './pages/Soporte';
import { Terminos } from './pages/Terminos';
import { Register } from './pages/Register';
import SignupWa from './pages/SignupWa';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionUpgrade from './pages/SubscriptionUpgrade';
import PricingPage from './pages/PricingPage';
import PromoPage from './pages/PromoPage';
import { capturePendingCouponFromUrl, clearPendingCoupon, getPendingCoupon } from './utils/pendingCoupon';
import { SuperAdminLoginPage } from './pages/SuperAdminLoginPage';
import { SuperAdminDashboardPage } from './pages/SuperAdminDashboardPage';
import { SATenants } from './pages/superadmin/Tenants';
import { SATenantEdit } from './pages/superadmin/TenantEdit';
import { SATenantCreate } from './pages/superadmin/TenantCreate';
import Payments from './pages/superadmin/Payments';
import Emails from './pages/superadmin/Emails';
import { SuperAdminLayout } from './layouts/SuperAdminLayout';
import { SuperAdminPrivateRoute } from './components/SuperAdminPrivateRoute';
import { Dashboard } from './pages/Dashboard';
import { PrivateRoute } from './components/PrivateRoute';
import { DashFlowTheme } from './theme/theme';
import { validateTokenAsync } from './store/authSlice';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Placeholder } from './pages/Placeholder';
import { ProductsPage } from './pages/ProductsPage';
import { InventoryPage } from './pages/InventoryPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { ChannelsPage } from './pages/ChannelsPage';
import { ReportsPage } from './pages/ReportsPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { StockMovementsPage } from './pages/StockMovementsPage';
import SalesPage from './pages/SalesPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { PurchasesPage } from './pages/PurchasesPage';
import { PurchaseRequestsPage } from './pages/PurchaseRequestsPage';
import { DebtRankingPage } from './pages/DebtRankingPage';
import { ReplenishmentPage } from './pages/ReplenishmentPage';
import { RecipesPage } from './pages/RecipesPage';
import { PricesPage } from './pages/PricesPage';
import { MaterialsPage } from './pages/MaterialsPage';
import { ServicesPage } from './pages/ServicesPage';
import { InsumosPage } from './pages/InsumosPage';
import { CustomersPage } from './pages/CustomersPage';
import { SellerActivitiesPage } from './pages/SellerActivitiesPage';
import { OpportunitiesPage } from './pages/OpportunitiesPage';
import { ProductionOrdersPage } from './pages/ProductionOrdersPage';
import { RetentionSamplesPage } from './pages/RetentionSamplesPage';
import { PublicTracePage } from './pages/PublicTracePage';
import { SellersPage } from './pages/SellersPage';
import CalendarPage from './pages/CalendarPage';
import { MlMappingPage } from './pages/MlMappingPage';
import ImportSalesPage from './pages/ImportSalesPage';
import ImportCustomersPage from './pages/ImportCustomersPage';
import ImportOpportunitiesPage from './pages/ImportOpportunitiesPage';
import ImportProspectosPage from './pages/ImportProspectosPage';
import { ProspectsPage } from './pages/ProspectsPage';
import { CashFlowPage } from './pages/CashFlowPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { PosPage } from './pages/PosPage';
import { AiPage } from './pages/AiPage';
import { CajaPage } from './pages/CajaPage';
import TodayPage from './pages/TodayPage';
import CommercialDashboardPage from './pages/CommercialDashboardPage';
import PriceListsPage from './pages/PriceListsPage';
import UninvoicedPage from './pages/UninvoicedPage';
import ChannelProfitabilityPage from './pages/ChannelProfitabilityPage';
import ProductionPlanPage from './pages/ProductionPlanPage';
import FactoryProductionPage from './pages/factory/FactoryProductionPage';
import FactoryCostsPage from './pages/factory/FactoryCostsPage';
import FactoryOutworkPage from './pages/factory/FactoryOutworkPage';
import FactoryCustomerOrdersPage from './pages/factory/FactoryCustomerOrdersPage';
import FactoryDebtsPage from './pages/factory/FactoryDebtsPage';
import FactoryStockPage from './pages/factory/FactoryStockPage';
import FactoryStoragePage from './pages/factory/FactoryStoragePage';
import FactoryMaintenancePage from './pages/factory/FactoryMaintenancePage';
import FactoryMaterialsPage from './pages/factory/FactoryMaterialsPage';
import FactoryProductLotsPage from './pages/factory/FactoryProductLotsPage';
import FactorySchedulePage from './pages/factory/FactorySchedulePage';
import FactoryLivePage from './pages/factory/FactoryLivePage';
import FactoryConfigPage from './pages/factory/FactoryConfigPage';
import ProfitabilityPage from './pages/ProfitabilityPage';
import FactoryPlantPage from './pages/factory/plant/FactoryPlantPage';
import FactoryTodayPage from './pages/factory/plant/FactoryTodayPage';
import FactoryPricesPage from './pages/factory/commercial/FactoryPricesPage';
import FactoryTaxBookPage from './pages/factory/commercial/FactoryTaxBookPage';
import FactoryCostClosePage from './pages/factory/commercial/FactoryCostClosePage';
import { PlantLayout } from './layouts/PlantLayout';
import { hasPlantShell } from './pages/factory/plant/layouts';
import { PageShell } from './components/ui/PageKit';
import CommercialSettingsPage from './pages/CommercialSettingsPage';
import ImportSpreadsheetPage from './pages/ImportSpreadsheetPage';
import MobileStockOutPage from './pages/mobile/MobileStockOutPage';
import MobileExpensePage from './pages/mobile/MobileExpensePage';
import MobileDeliveriesPage from './pages/mobile/MobileDeliveriesPage';
import LotProfitabilityPage from './pages/LotProfitabilityPage';
import MobileLayout from './layouts/MobileLayout';
import MobileHomePage from './pages/mobile/MobileHomePage';
import MobileSellPage from './pages/mobile/MobileSellPage';
import MobileCollectPage from './pages/mobile/MobileCollectPage';
import MobileProducePage from './pages/mobile/MobileProducePage';
import MobileStockPage from './pages/mobile/MobileStockPage';
import { PurchaseSuggestionsPage } from './pages/PurchaseSuggestionsPage';
import { CompletarPerfil } from './pages/CompletarPerfil';
import LandingPage from './pages/LandingPage';
import AyudaIndexPage from './pages/ayuda/AyudaIndexPage';
import AyudaLessonPage from './pages/ayuda/AyudaLessonPage';
import { syncTrackingWithRoute } from './utils/tracking';
import PublicWhatsAppFloat from './components/public/WhatsAppFloat';

/**
 * Root gate: show the public landing page to anonymous visitors and send
 * authenticated users straight to their dashboard. Kept simple because the
 * actual role-based dashboard routing already lives under the layout routes.
 */
const RootGate: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
};

// Title y description propios de cada página pública (index.html trae los de la home).
const PUBLIC_META: Record<string, { title: string; description?: string }> = {
  '/precios': {
    title: 'Precios de DashFlow: planes para comercios',
    description: 'Planes de DashFlow para controlar ventas, stock y facturación ARCA de tu comercio. Probalo 7 días gratis, sin tarjeta. Cancelás cuando quieras.',
  },
  '/register': { title: 'Creá tu cuenta en DashFlow: gratis, sin tarjeta' },
  '/soporte': { title: 'Soporte de DashFlow' },
  '/privacidad': { title: 'Política de privacidad | DashFlow' },
  '/terminos': { title: 'Términos y condiciones | DashFlow' },
};

const PublicPageMeta: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const descTag = document.querySelector('meta[name="description"]');
    const defaults = { title: document.title, description: descTag?.getAttribute('content') || '' };
    const meta = PUBLIC_META[pathname.replace(/\/+$/, '')];
    if (!meta) return;
    document.title = meta.title;
    if (meta.description) descTag?.setAttribute('content', meta.description);
    return () => {
      document.title = defaults.title;
      descTag?.setAttribute('content', defaults.description);
    };
  }, [pathname]);
  return null;
};

// GA4 + Clarity: page_view en rutas públicas y apagado en el panel (ver utils/tracking.ts).
const RouteTracking: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    syncTrackingWithRoute(pathname);
  }, [pathname]);
  return null;
};

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, token, user, tenant } = useSelector((state: RootState) => state.auth);
  const userRole = (user?.role || '').toLowerCase();
  // Parriplast (Asener) tiene su propio sistema de planta: otra barra, otro menú y el tablero del día como inicio.
  const plantShell = hasPlantShell(tenant);
  // Vendedor de una empresa con la bandera "seller-extended-access": usa el layout de
  // administracion (secciones Comercial/Catalogo/Operaciones/...) en vez del SellerLayout.
  // null = todavia consultando; si la consulta falla (403 sin bandera) queda el layout de siempre.
  const [sellerExtended, setSellerExtended] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isAuthenticated || userRole !== 'seller') {
      setSellerExtended(null);
      return;
    }
    let cancelled = false;
    api.getFeatureFlags()
      .then(flags => {
        if (!cancelled) setSellerExtended((flags || []).some(f => f.enabled && f.key.toLowerCase() === SELLER_EXTENDED_FLAG));
      })
      .catch(() => {
        if (!cancelled) setSellerExtended(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, userRole]);
  const isSeller = (userRole === 'seller' && !sellerExtended) || userRole === 'vendedormarcapropia';

  // Cupón de /promo (?cupon=): se guarda al entrar y, ya logueado, se deja pendiente en el tenant
  // (POST /checkout/pending-coupon) para que el backend lo aplique cuando se suscriba.
  useEffect(() => {
    capturePendingCouponFromUrl();
  }, []);
  useEffect(() => {
    if (!isAuthenticated) return;
    const code = getPendingCoupon();
    if (!code) return;
    (api as any).api
      .post('/checkout/pending-coupon', { code })
      .then(() => clearPendingCoupon())
      .catch((err: any) => {
        // 404 = código inválido/vencido, 409 = la cuenta ya usó uno: no reintentar.
        const st = err?.response?.status;
        if (st === 404 || st === 409) clearPendingCoupon();
      });
  }, [isAuthenticated]);

  useEffect(() => {
    // Check if user has a stored token and validate it
    if (token && !isAuthenticated) {
      dispatch(validateTokenAsync());
    }
  }, [dispatch, token, isAuthenticated]);

  // Show loading screen while validating token. Sin esperar a "loading": en el primer render todavía no arrancó la
  // validación y las rutas privadas mandaban a /login, así que recargar cualquier pantalla terminaba en el tablero.
  // Si el token no sirve, la validación lo borra (token = null) y se sale de esta pantalla.
  if (token && !isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Validando sesión...
        </Typography>
      </Box>
    );
  }

  if (isAuthenticated && userRole === 'seller' && sellerExtended === null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Router>
      <PublicPageMeta />
      <RouteTracking />
      <PublicWhatsAppFloat />
      <Routes>
        <Route path="/trace/:batchId" element={<PublicTracePage />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/soporte" element={<Soporte />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/signup-wa" element={<SignupWa />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/upgrade" element={<SubscriptionUpgrade />} />
        {/* Paywall / pricing público — alto en conversión, cableado al checkout existente. */}
        <Route path="/precios" element={<PricingPage />} />
        <Route path="/ayuda" element={<AyudaIndexPage />} />
        <Route path="/ayuda/:slug" element={<AyudaLessonPage />} />
        <Route path="/promo" element={<PromoPage />} />
        <Route path="/pricing" element={<Navigate to="/precios" replace />} />
        {/*
          Unified onboarding wizard. Admin-only: sellers are onboarded by their
          admin so they never hit this route (PrivateRoute + allowedRoles handles
          the role gating — non-admins are bounced to /dashboard).
        */}
        <Route
          path="/completar-perfil"
          element={
            <PrivateRoute allowedRoles={["Admin"]}>
              <CompletarPerfil />
            </PrivateRoute>
          }
        />
        {/* Super Admin routes */}
        <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
        <Route
          path="/super-admin"
          element={
            <SuperAdminPrivateRoute>
              <SuperAdminLayout />
            </SuperAdminPrivateRoute>
          }
        >
          <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
          <Route path="dashboard" element={<SuperAdminDashboardPage />} />
          <Route path="tenants" element={<SATenants />} />
          <Route path="tenants/new" element={<SATenantCreate />} />
          <Route path="tenants/:id" element={<SATenantEdit />} />
          <Route path="payments" element={<Payments />} />
          <Route path="emails" element={<Emails />} />
        </Route>
        {/* Modo celular: botones grandes, sin menú lateral (flag "mobile-mode" muestra el acceso). */}
        <Route
          path="/m"
          element={
            <PrivateRoute>
              <MobileLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<MobileHomePage />} />
          <Route path="vender" element={<MobileSellPage />} />
          <Route path="cobrar" element={<MobileCollectPage />} />
          <Route path="producir" element={<MobileProducePage />} />
          <Route path="stock" element={<MobileStockPage />} />
          <Route path="hoy" element={<TodayPage basePath="/m" />} />
          <Route path="salida" element={<MobileStockOutPage />} />
          <Route path="entregas" element={<MobileDeliveriesPage />} />
          <Route path="gasto" element={<MobileExpensePage />} />
        </Route>
        {/* Public landing — shown first to anonymous visitors, bounced to /dashboard for authenticated users. */}
        <Route path="/" element={<RootGate />} />
        {isSeller ? (
          <Route
            element={
              <PrivateRoute>
                <SellerLayout />
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="today" element={<TodayPage />} />
            <Route path="opportunities" element={<OpportunitiesPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="pos" element={<PosPage />} />
            <Route path="caja" element={<CajaPage />} />
            <Route path="activities" element={<SellerActivitiesPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="prospects" element={<ProspectsPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="replenishment" element={<ReplenishmentPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="profitability" element={<ProfitabilityPage />} />
            <Route path="cash-flow" element={<CashFlowPage />} />
          </Route>
        ) : plantShell ? (
          <Route
            element={
              <PrivateRoute>
                <PlantLayout />
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<FactoryTodayPage />} />
            <Route path="factory/plant" element={<FactoryPlantPage />} />
            <Route path="stock-movements" element={<StockMovementsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="suppliers" element={<SuppliersPage />} />
            <Route path="purchases" element={<PurchasesPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="notification-settings" element={<NotificationSettingsPage />} />
            <Route element={<PageShell />}>
              <Route path="factory/production" element={<FactoryProductionPage />} />
              <Route path="factory/costs" element={<FactoryCostsPage />} />
              <Route path="factory/outwork" element={<FactoryOutworkPage />} />
              <Route path="factory/customer-orders" element={<FactoryCustomerOrdersPage />} />
              <Route path="factory/debts" element={<FactoryDebtsPage />} />
              <Route path="factory/stock" element={<FactoryStockPage />} />
              <Route path="factory/storage" element={<FactoryStoragePage />} />
              <Route path="factory/maintenance" element={<FactoryMaintenancePage />} />
              <Route path="factory/materials" element={<FactoryMaterialsPage />} />
              <Route path="factory/product-lots" element={<FactoryProductLotsPage />} />
              <Route path="factory/schedule" element={<FactorySchedulePage />} />
              <Route path="factory/live" element={<FactoryLivePage />} />
              <Route path="factory/prices" element={<FactoryPricesPage />} />
              <Route path="factory/cost-close" element={<FactoryCostClosePage />} />
              <Route path="factory/tax-book" element={<FactoryTaxBookPage />} />
              <Route path="factory/config" element={<FactoryConfigPage />} />
              {/* Cada vista de una pantalla es un ítem del menú: /factory/production/indicadores, etc. */}
              <Route path="factory/production/:view" element={<FactoryProductionPage />} />
              <Route path="factory/product-lots/:view" element={<FactoryProductLotsPage />} />
              <Route path="factory/materials/:view" element={<FactoryMaterialsPage />} />
              <Route path="factory/maintenance/:view" element={<FactoryMaintenancePage />} />
              <Route path="factory/outwork/:view" element={<FactoryOutworkPage />} />
              <Route path="factory/storage/:view" element={<FactoryStoragePage />} />
              <Route path="factory/customer-orders/:view" element={<FactoryCustomerOrdersPage />} />
              <Route path="factory/stock/:view" element={<FactoryStockPage />} />
            </Route>
          </Route>
        ) : (
          <Route
            element={
              <PrivateRoute>
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="production-orders" element={<ProductionOrdersPage />} />
            <Route path="retention-samples" element={<RetentionSamplesPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="pos" element={<PosPage />} />
            <Route path="caja" element={<CajaPage />} />
            <Route path="ai" element={<AiPage />} />
            <Route path="sales/import" element={<ImportSalesPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="channels" element={<ChannelsPage />} />
            <Route path="ml-mapping" element={<MlMappingPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/import" element={<ImportCustomersPage />} />
            <Route path="prospects" element={<ProspectsPage />} />
            <Route path="prospects/import" element={<ImportProspectosPage />} />
            <Route path="activities" element={<SellerActivitiesPage />} />
            <Route path="opportunities" element={<OpportunitiesPage />} />
            <Route path="opportunities/import" element={<ImportOpportunitiesPage />} />
            <Route path="replenishment" element={<ReplenishmentPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="profitability" element={<ProfitabilityPage />} />
            <Route path="recipes" element={<RecipesPage />} />
            <Route path="prices" element={<PricesPage />} />
            <Route path="materials" element={<MaterialsPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="insumos" element={<InsumosPage />} />
            <Route path="suppliers" element={<SuppliersPage />} />
            <Route path="purchases" element={<PurchasesPage />} />
            <Route path="purchase-requests" element={<PurchaseRequestsPage />} />
            <Route path="debt-ranking" element={<DebtRankingPage />} />
            <Route path="intelligence/purchase-suggestions" element={<PurchaseSuggestionsPage />} />
            <Route path="sellers" element={<SellersPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="notification-settings" element={<NotificationSettingsPage />} />
            <Route path="stock-movements" element={<StockMovementsPage />} />
            <Route path="cash-flow" element={<CashFlowPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="today" element={<TodayPage />} />
            <Route path="lot-profitability" element={<LotProfitabilityPage />} />
            <Route path="commercial-dashboard" element={<CommercialDashboardPage />} />
            <Route path="price-lists" element={<PriceListsPage />} />
            <Route path="uninvoiced" element={<UninvoicedPage />} />
            <Route path="channel-profitability" element={<ChannelProfitabilityPage />} />
            <Route path="production-plan" element={<ProductionPlanPage />} />
            <Route path="commercial-settings" element={<CommercialSettingsPage />} />
            <Route path="import-spreadsheet" element={<ImportSpreadsheetPage />} />
            <Route path="factory/plant" element={<FactoryPlantPage />} />
            <Route element={<PageShell />}>
              <Route path="factory/production" element={<FactoryProductionPage />} />
              <Route path="factory/costs" element={<FactoryCostsPage />} />
              <Route path="factory/outwork" element={<FactoryOutworkPage />} />
              <Route path="factory/customer-orders" element={<FactoryCustomerOrdersPage />} />
              <Route path="factory/debts" element={<FactoryDebtsPage />} />
              <Route path="factory/stock" element={<FactoryStockPage />} />
              <Route path="factory/storage" element={<FactoryStoragePage />} />
              <Route path="factory/maintenance" element={<FactoryMaintenancePage />} />
              <Route path="factory/materials" element={<FactoryMaterialsPage />} />
              <Route path="factory/product-lots" element={<FactoryProductLotsPage />} />
              <Route path="factory/schedule" element={<FactorySchedulePage />} />
              <Route path="factory/live" element={<FactoryLivePage />} />
            </Route>
          </Route>
        )}
        {/* Catch all — unknown paths fall back to the root gate, which picks landing or dashboard based on auth. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={DashFlowTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <CssBaseline />
          <AppContent />
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
